import Chart from 'react-apexcharts';
import numeral from 'numeral';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';
import { Box, Card, CardHeader, CardContent, CircularProgress, FormControl, IconButton, MenuItem, Select, Skeleton, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useState, useEffect } from 'react';
import { dashboardApi } from '../../../api/dashboardApi';
import MonthlyRevenueReportPDF from './MonthlyRevenueReportPDF';
import useMounted from '../../../hooks/useMounted';
import { FINANCIER_ORDER } from '../../../hooks/useFinanciers';
import { getTone, getFinancierTone, toneAlpha, lightVeil, darkVeil } from '../../../theme/tones';

const MONTHS = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const FIRST_YEAR = 2023;
// Valeur du sélecteur de mois pour « Toute l'année » (même convention que le tableau des commerciaux).
const WHOLE_YEAR = 'all';

const periodLabelOf = (month, year) => (month === WHOLE_YEAR ? `Année ${year}` : `${MONTHS[month - 1]} ${year}`);

const formatEuro = (value) => `${numeral(value || 0).format('0,000,000.00').replaceAll(',', ' ')} €`;
const formatEuroRond = (value) => `${numeral(value || 0).format('0,000,000').replaceAll(',', ' ')} €`;

// Ordre imposé (DPC d'abord), les financeurs hors liste à la suite.
const orderIndex = (name) => {
  const index = FINANCIER_ORDER.indexOf(name);
  return index === -1 ? FINANCIER_ORDER.length : index;
};

const selectSx = {
  borderRadius: '8px',
  height: 32,
  fontSize: '0.78rem',
  fontWeight: 700
};

const MonthlyRevenueBreakdown = () => {
  const theme = useTheme();
  const mounted = useMounted();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(1);
  const [total, setTotal] = useState({ ca_net: 0, ca_brut: 0, count_net: 0, count_brut: 0 });
  const [financiers, setFinanciers] = useState([]);
  const [system, setSystem] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const years = [];
  for (let y = now.getFullYear(); y >= FIRST_YEAR; y -= 1) {
    years.push(y);
  }

  useEffect(() => {
    const getMonthlyRevenue = async () => {
      setLoading(true);
      try {
        const data = await dashboardApi.getMonthlyRevenue(localStorage.getItem('accessToken'), year, month);

        if (!data || !data.total) {
          throw new Error('Impossible de récupérer le chiffre d\'affaires de la période.');
        }

        if (mounted.current) {
          setTotal(data.total);
          setFinanciers([...(data.financiers || [])].sort((a, b) => orderIndex(a.name) - orderIndex(b.name)));
          setSystem(data.system || null);
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted.current) {
          setTotal({ ca_net: 0, ca_brut: 0, count_net: 0, count_brut: 0 });
          setFinanciers([]);
          setSystem(null);
          setLoading(false);
        }
      }
    };
    getMonthlyRevenue();
  }, [mounted, year, month, reload]);

  useEffect(() => {
    const interval = setInterval(() => setReload((prevReload) => prevReload + 1), 30000);
    return () => clearInterval(interval);
  }, []);

  // PDF de la période affichée (mois ou année) : KPIs + financeurs (déjà chargés) et CA de chaque
  // commercial, demandé au moment du clic pour le même mois et la même année.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const commercials = await dashboardApi.getCommercialRevenue(localStorage.getItem('accessToken'), String(year), String(month));
      const fileSuffix = month === WHOLE_YEAR ? '' : `-${String(month).padStart(2, '0')}`;
      const generatedAt = new Date().toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
      const blob = await pdf(
        <MonthlyRevenueReportPDF
          periodLabel={periodLabelOf(month, year)}
          generatedAt={generatedAt}
          total={total}
          financiers={financiers}
          system={system}
          commercials={Array.isArray(commercials) ? commercials : []}
        />
      ).toBlob();
      saveAs(blob, `CA_${year}${fileSuffix}.pdf`);
    } catch (err) {
      console.error(err);
      toast.error('Le PDF n\'a pas pu être généré.');
    } finally {
      if (mounted.current) {
        setDownloading(false);
      }
    }
  };

  const series = financiers
    .filter((financier) => Number(financier.ca_net) > 0)
    .map((financier) => ({
      color: getTone(getFinancierTone(financier.name)).base,
      label: financier.name,
      value: Number(financier.ca_net)
    }));

  const chartOptions = {
    chart: {
      background: 'transparent',
      stacked: false,
      toolbar: {
        show: false
      }
    },
    colors: series.map((item) => item.color),
    dataLabels: {
      enabled: true
    },
    labels: series.map((item) => item.label),
    legend: {
      show: false
    },
    plotOptions: {
      pie: {
        // Par défaut ApexCharts masque l'étiquette des tranches de moins de 10°
        // (ex. FIFPL-PAIEMENT à 1,5 %) : on les affiche toutes.
        dataLabels: {
          minAngleToShowLabel: 0
        },
        donut: {
          size: '72',
          labels: {
            show: true,
            name: {
              show: false
            },
            // Le centre ne fait qu'une centaine de pixels : à la taille par
            // défaut (22 px) et avec les centimes, le montant débordait sur
            // l'anneau. Le montant exact reste affiché dans la tuile CA net.
            value: {
              show: true,
              fontSize: '14px',
              fontWeight: 700,
              offsetY: 5,
              formatter(val) {
                return formatEuroRond(val);
              }
            },
            total: {
              show: true,
              showAlways: true,
              formatter(w) {
                return formatEuroRond(w.globals.seriesTotals.reduce((a, b) => a + b, 0));
              }
            }
          }
        }
      }
    },
    stroke: {
      colors: [theme.palette.background.paper],
      width: 1
    },
    theme: {
      mode: theme.palette.mode
    }
  };
  // ApexCharts (donut) plante sur une série vide : on affiche un message à la place.
  const hasChartData = series.length > 0;

  const tiles = [
    { key: 'net', label: 'CA net', value: total.ca_net, count: total.count_net, tone: 'emerald' },
    { key: 'brut', label: 'CA brut', value: total.ca_brut, count: total.count_brut, tone: 'neutral' }
  ];

  return (
    <Card
      sx={{
        borderRadius: '16px',
        border: (t) => `1px solid ${t.palette.divider}`,
        boxShadow: (t) => (t.palette.mode === 'light'
          ? `0 4px 18px 0 ${darkVeil(0.03)}`
          : `0 4px 18px 0 ${darkVeil(0.2)}`),
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 2, pt: 1 }}>
        <CardHeader
          title={month === WHOLE_YEAR ? `CA de l'année (${year})` : `CA du mois (${periodLabelOf(month, year)})`}
          sx={{ px: 0, flexGrow: 1, '& .MuiCardHeader-title': { fontSize: '1.05rem', fontWeight: 700 } }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small">
            <Select
              value={month}
              onChange={(e) => setMonth(e.target.value === WHOLE_YEAR ? WHOLE_YEAR : Number(e.target.value))}
              sx={{ ...selectSx, minWidth: 110 }}
              inputProps={{ 'aria-label': 'Mois' }}
            >
              <MenuItem value={WHOLE_YEAR}>Toute l&apos;année</MenuItem>
              {MONTHS.map((label, index) => (
                <MenuItem key={label} value={index + 1}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <Select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ ...selectSx, minWidth: 80 }}
              inputProps={{ 'aria-label': 'Année' }}
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Télécharger le PDF de la période">
            <span>
              <IconButton
                onClick={handleDownload}
                disabled={loading || downloading}
                aria-label="Télécharger le PDF de la période"
                sx={{
                  height: 32,
                  width: 32,
                  borderRadius: '8px',
                  border: `1px solid ${theme.palette.divider}`
                }}
              >
                {downloading ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1, pb: '16px !important' }}>
        {/* CA NET / BRUT DU MOIS */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
          {tiles.map((tile) => (
            <Box
              key={tile.key}
              sx={{
                p: 1.5,
                borderRadius: '12px',
                backgroundColor: toneAlpha(tile.tone, theme.palette.mode === 'dark' ? 0.15 : 0.08),
                border: `1px solid ${toneAlpha(tile.tone, 0.2)}`
              }}
            >
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                {tile.label}
              </Typography>
              {!loading ? (
                <>
                  <Typography
                    sx={{
                      fontSize: '1.05rem',
                      fontWeight: 800,
                      color: tile.key === 'net' ? 'success.main' : 'text.primary',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {formatEuro(tile.value)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
                    {tile.count} {tile.count > 1 ? 'inscriptions' : 'inscription'}
                  </Typography>
                </>
              ) : (
                <Skeleton animation="wave" variant="text" height={42} />
              )}
            </Box>
          ))}
        </Box>

        {/* RÉPARTITION DU CA NET PAR FINANCEUR */}
        {!loading ? (
          <Box sx={{ minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {hasChartData ? (
              <Chart
                height="180"
                width="100%"
                options={chartOptions}
                series={series.map((item) => item.value)}
                type="donut"
              />
            ) : (
              <Typography color="text.secondary" variant="body2">
                Aucune donnée pour cette période
              </Typography>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <Skeleton animation="wave" variant="circular" width={150} height={150} />
          </Box>
        )}

        {/* CA NET / BRUT PAR FINANCEUR */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Box sx={{ display: 'flex', px: 1.5 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
              Par financeur
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
              Net / Brut
            </Typography>
          </Box>
          {loading && [0, 1, 2].map((i) => (
            <Skeleton key={i} animation="wave" variant="text" height={32} />
          ))}
          {!loading && financiers.map((financier) => {
            const tone = getFinancierTone(financier.name);
            const color = getTone(tone).base;
            // Les petites parts n'ont pas d'étiquette sur le donut (tranche trop fine) :
            // le pourcentage est donc repris ici, pour chaque financeur.
            const share = Number(total.ca_net) > 0 ? (Number(financier.ca_net) / Number(total.ca_net)) * 100 : 0;
            return (
              <Box
                key={financier.name}
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  py: 0.5,
                  px: 1.5,
                  borderRadius: '10px',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'light' ? darkVeil(0.02) : lightVeil(0.02)
                  }
                }}
              >
                <Box
                  sx={{
                    backgroundColor: color,
                    borderRadius: '50%',
                    height: 10,
                    width: 10,
                    boxShadow: `0 0 8px ${color}`,
                    flexShrink: 0
                  }}
                />
                <Box sx={{ ml: 2, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                      {financier.name}
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: toneAlpha(tone, theme.palette.mode === 'dark' ? 0.2 : 0.12),
                        color: theme.palette.mode === 'dark' ? (getTone(tone).light || getTone(tone).base) : getTone(tone).deep,
                        borderRadius: '16px',
                        px: 0.75,
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        lineHeight: '18px'
                      }}
                    >
                      {`${share.toFixed(1)}%`}
                    </Box>
                  </Box>
                  <Typography color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                    {financier.count_net} / {financier.count_brut} {financier.count_brut > 1 ? 'inscriptions' : 'inscription'}
                  </Typography>
                </Box>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: 'success.main' }}>
                    {formatEuro(financier.ca_net)}
                  </Typography>
                  <Typography color="text.secondary" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                    {formatEuro(financier.ca_brut)}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MonthlyRevenueBreakdown;
